import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";

import moment from "moment";

import Langs, { Localize } from "../../app/lang/langs";
import NetworkManager from "../../app/managers/networkManager";
import { showErrorToast, showSuccessToast } from "../../app/utils/helpers";
import {
    deleteVLearnAnnouncement, listVLearnAnnouncements,
    markVLearnAnnouncementRead, saveVLearnAnnouncement
} from "../../containers/vlearn/vlearnSlice";
import {
    VLearnEmptyState, VLearnProgressBar, vlearnSurface, vlearnType
} from "./vlearnUi";

const EMPTY_FORM = { id: null, title: "", body: "", is_pinned: false, notify_email: false }

/**
 * The server's own message for a failed call, or null.
 *
 * `postRequest` resolves `{ responseError: error.response }`, the axios response object, so a
 * 422's body sits at `.data.error`. Surfacing that rather than a generic "could not save" is
 * the difference between "it did not work" and "an announcement needs a title".
 */
const serverError = (payload) => (payload
    && payload.responseError
    && payload.responseError.data
    && payload.responseError.data.error) || null

/**
 * Course announcements.
 *
 * Rows come from `VLearnProgressController.listAnnouncements`, already ordered pinned-first
 * then newest — the same order they are rendered in, deliberately, so the screen agrees with
 * whatever a second reader sees. `read_count` / `total_enrolled` are only present when the
 * server decided the caller may report on the course; a learner's response simply does not
 * carry them, which is why the read bar is drawn from their presence rather than from a prop.
 *
 * ── The body is never HTML ────────────────────────────────────────────────────────────
 *
 * `body` is free text typed by a course author and stored verbatim. It is rendered as text
 * with `whiteSpace: 'pre-wrap'`, which keeps the author's line breaks without interpreting
 * anything. Passing it to `dangerouslySetInnerHTML` would turn any author — or anyone who
 * ever compromises an author's session — into a stored-XSS vector against every learner
 * enrolled on the course, and the value being "our own staff wrote it" is not a defence:
 * stored XSS is exactly the vulnerability whose payload arrives through a trusted author
 * field. There is no version of this component that renders the field as markup.
 *
 * @param {object} props
 * @param {number} props.idCourse
 * @param {boolean} props.canEdit Whether this user may post, edit and delete. The server
 *   re-checks it on every call; this only decides what is worth drawing.
 */
export default function AnnouncementsPanel({ idCourse, canEdit }) {

    const dispatch = useDispatch()

    const announcements = useSelector((state) => state.vlearn.announcements)
    const emailEnabled = useSelector((state) => state.vlearn.settings.announcements_email_enabled)
    const lang = useSelector((state) => state.settings.lang)

    // `isMobile === 2` is the platform's phone flag; the media query covers a narrowed
    // desktop window. Either one makes the compose dialog full-screen.
    const isMobileRedux = useSelector((state) => state.settings.isMobile)
    const theme = useTheme()
    const isSmallQuery = useMediaQuery(theme.breakpoints.down('sm'))
    const isSmall = isMobileRedux === 2 || isSmallQuery

    const [form, setForm] = useState(EMPTY_FORM)
    const [composeOpen, setComposeOpen] = useState(false)
    const [titleError, setTitleError] = useState(false)
    const [pendingDelete, setPendingDelete] = useState(null)

    // Keyed on the course, so React 18's StrictMode double mount is one request and a change
    // of course is a fresh one.
    const fetchedFor = useRef(null)

    useEffect(() => {
        if (!idCourse) return
        if (fetchedFor.current === idCourse) return
        fetchedFor.current = idCourse
        dispatch(listVLearnAnnouncements({ id_course: idCourse }))
    }, [dispatch, idCourse])

    // Pinned first, then newest. The server already sorts this way; re-applying it locally
    // costs nothing and keeps the order correct after an optimistic re-render.
    const rows = useMemo(() => {
        return [...(announcements || [])].sort((a, b) => {
            const pinnedA = Number(a.is_pinned) || 0
            const pinnedB = Number(b.is_pinned) || 0
            if (pinnedA !== pinnedB) return pinnedB - pinnedA
            const dateA = new Date(a.published_at || a.created_at || 0).getTime()
            const dateB = new Date(b.published_at || b.created_at || 0).getTime()
            return dateB - dateA
        })
    }, [announcements])

    const formatDate = useCallback((value) => {
        if (!value) return null
        const parsed = moment(value)
        if (!parsed.isValid()) return null
        // Instance-level locale rather than `moment.locale(...)`: the global is set once from
        // the stored language in app/lang/langs.js and belongs to the whole application.
        return parsed.locale(lang || 'fr').format('DD/MM/YYYY HH:mm')
    }, [lang])

    // ── Read receipts for learners ───────────────────────────────────────────────────
    //
    // `markAnnouncementRead` is a learner action — the server refuses it for someone who can
    // only report on the course, and an author reading their own post is not one of the
    // "11 of 14". So the observer is only wired up for a caller who is not the author, and
    // the ids already marked are remembered here to avoid re-posting a receipt the server
    // would only ignore.
    const marked = useRef(new Set())
    const itemRefs = useRef(new Map())

    useEffect(() => {
        if (canEdit) return
        if (typeof IntersectionObserver === 'undefined') return

        const unread = rows.filter((row) => !row.is_read && !marked.current.has(String(row.id)))
        if (!unread.length) return

        const observer = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                if (!entry.isIntersecting) continue
                const id = entry.target.getAttribute('data-announcement-id')
                if (!id || marked.current.has(id)) continue
                marked.current.add(id)
                observer.unobserve(entry.target)
                dispatch(markVLearnAnnouncementRead({ id_announcement: Number(id) }))
            }
        }, { threshold: 0.6 })

        for (const row of unread) {
            const node = itemRefs.current.get(String(row.id))
            if (node) observer.observe(node)
        }
        return () => observer.disconnect()
    }, [dispatch, rows, canEdit])

    // ── Compose / edit ──────────────────────────────────────────────────────────────

    const openCompose = useCallback((row) => {
        setTitleError(false)
        setForm(row
            ? {
                id: row.id,
                title: row.title || "",
                body: row.body || "",
                is_pinned: Boolean(Number(row.is_pinned)),
                notify_email: Boolean(Number(row.notify_email))
            }
            : EMPTY_FORM)
        setComposeOpen(true)
    }, [])

    const closeCompose = useCallback(() => {
        setComposeOpen(false)
        setForm(EMPTY_FORM)
        setTitleError(false)
    }, [])

    const onSave = useCallback(async () => {
        const title = form.title.trim()
        if (!title) {
            setTitleError(true)
            showErrorToast(Localize("VLEARN_ANNOUNCEMENT_TITLE_REQUIRED", lang))
            return
        }
        const response = await dispatch(saveVLearnAnnouncement({
            id_course: idCourse,
            id_announcement: form.id || undefined,
            title,
            body: form.body,
            is_pinned: form.is_pinned,
            notify_email: form.notify_email
        }))
        // The thunks in this slice never reject: they resolve with the network layer's own
        // object, so a failure arrives on `.fulfilled` as `responseError` and the server's
        // message is the one worth showing.
        const payload = response.payload || {}
        const data = payload.responseData
        if (!data || data.status !== 1) {
            const message = serverError(payload) || Localize("VLEARN_SAVE_FAILED", lang)
            showErrorToast(message)
            return
        }
        showSuccessToast(Localize("VLEARN_ANNOUNCEMENT_SAVED", lang))
        closeCompose()
        dispatch(listVLearnAnnouncements({ id_course: idCourse }))
    }, [dispatch, form, idCourse, lang, closeCompose])

    const onConfirmDelete = useCallback(async () => {
        if (!pendingDelete) return
        const response = await dispatch(deleteVLearnAnnouncement({
            id_course: idCourse,
            id_announcement: pendingDelete.id
        }))
        const payload = response.payload || {}
        const data = payload.responseData
        setPendingDelete(null)
        if (!data || data.status !== 1) {
            const message = serverError(payload) || Localize("VLEARN_SAVE_FAILED", lang)
            showErrorToast(message)
            return
        }
        showSuccessToast(Localize("VLEARN_ANNOUNCEMENT_DELETED", lang))
        dispatch(listVLearnAnnouncements({ id_course: idCourse }))
    }, [dispatch, idCourse, lang, pendingDelete])

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>

            {/*
              * The heading sits in a `vlearnSurface.panel` like every sibling tab's does —
              * Audience, Instructors and Details all open with one. This tab was the only
              * bare heading in the editor, so it had none of the panel's padding and sat
              * flush against the tab strip.
              *
              * The announcement LIST below stays outside it: those are individual cards, and
              * nesting a stack of cards inside a panel is not what the other tabs do.
              */}
            <Box sx={{
                ...vlearnSurface.panel,
                display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap'
            }}>
                <Typography component="h2" sx={{ ...vlearnType.sectionTitle, flex: 1 }}>
                    <Langs str="VLEARN_ANNOUNCEMENTS_TITLE" />
                </Typography>
                {canEdit ? (
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddRoundedIcon />}
                        onClick={() => openCompose(null)}
                        sx={{ textTransform: 'none' }}
                    >
                        <Langs str="VLEARN_ANNOUNCEMENT_NEW" />
                    </Button>
                ) : null}
            </Box>

            {rows.length === 0 ? (
                <Box sx={{ ...vlearnSurface.card }}>
                    <VLearnEmptyState
                        icon={<CampaignOutlinedIcon />}
                        title={<Langs str="VLEARN_NO_ANNOUNCEMENTS_TITLE" />}
                        description={<Langs str={canEdit
                            ? "VLEARN_NO_ANNOUNCEMENTS_BODY_AUTHOR"
                            : "VLEARN_NO_ANNOUNCEMENTS_BODY_LEARNER"} />}
                    />
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {rows.map((row) => {
                        const pinned = Boolean(Number(row.is_pinned))
                        // Only a caller the server decided may report on the course receives
                        // these two fields at all.
                        const hasReceipts = typeof row.read_count !== 'undefined'
                            && typeof row.total_enrolled !== 'undefined'
                        const readCount = Number(row.read_count) || 0
                        const totalEnrolled = Number(row.total_enrolled) || 0
                        const readPercent = totalEnrolled > 0
                            ? Math.round((readCount / totalEnrolled) * 100) : 0
                        const unread = !canEdit && !row.is_read
                        const authorPhoto = NetworkManager.getInstance()
                            .getCollabPhotoUrl(row.created_by, row.author_photo_updated_at)
                        const published = formatDate(row.published_at || row.created_at)
                        const emailed = formatDate(row.emailed_at)

                        return (
                            <Box
                                key={row.id}
                                component="article"
                                data-announcement-id={row.id}
                                ref={(node) => {
                                    if (node) itemRefs.current.set(String(row.id), node)
                                    else itemRefs.current.delete(String(row.id))
                                }}
                                sx={{
                                    ...vlearnSurface.card,
                                    padding: '16px 18px',
                                    // Unread is marked by a left rail AND an "Unread" chip:
                                    // the rail alone would be a colour-only encoding.
                                    borderLeft: unread
                                        ? '3px solid var(--orange, rgb(255,144,21))'
                                        : '1px solid rgba(0,0,0,0.08)'
                                }}
                            >
                                <Box sx={{
                                    display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap'
                                }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box sx={{
                                            display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap'
                                        }}>
                                            {pinned ? (
                                                <Chip
                                                    size="small"
                                                    icon={<PushPinOutlinedIcon sx={{ fontSize: 13 }} />}
                                                    label={<Langs str="VLEARN_ANNOUNCEMENT_PINNED_BADGE" />}
                                                    sx={{
                                                        height: 20, fontSize: '0.68rem', fontWeight: 700,
                                                        borderRadius: '7px',
                                                        backgroundColor: 'rgba(255,144,21,0.14)',
                                                        color: '#B25E00',
                                                        '& .MuiChip-icon': { color: '#B25E00' }
                                                    }}
                                                />
                                            ) : null}
                                            {unread ? (
                                                <Chip
                                                    size="small"
                                                    label={<Langs str="VLEARN_ANNOUNCEMENT_UNREAD" />}
                                                    sx={{
                                                        height: 20, fontSize: '0.68rem', fontWeight: 700,
                                                        borderRadius: '7px',
                                                        backgroundColor: 'rgba(0,40,128,0.09)',
                                                        color: 'var(--vital-blue, #002880)'
                                                    }}
                                                />
                                            ) : null}
                                        </Box>

                                        <Typography component="h3" sx={{
                                            ...vlearnType.sectionTitle, fontSize: '0.98rem', mt: pinned || unread ? 0.6 : 0
                                        }}>
                                            {row.title}
                                        </Typography>
                                    </Box>

                                    {canEdit ? (
                                        <Box sx={{ display: 'flex', gap: 0.25 }}>
                                            <Tooltip title={Localize("VLEARN_ANNOUNCEMENT_EDIT_ARIA", lang)} arrow>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => openCompose(row)}
                                                    aria-label={Localize("VLEARN_ANNOUNCEMENT_EDIT_ARIA", lang)}
                                                >
                                                    <EditOutlinedIcon sx={{ fontSize: 17 }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={Localize("VLEARN_ANNOUNCEMENT_DELETE_ARIA", lang)} arrow>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setPendingDelete(row)}
                                                    aria-label={Localize("VLEARN_ANNOUNCEMENT_DELETE_ARIA", lang)}
                                                >
                                                    <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    ) : null}
                                </Box>

                                {/*
                                  * Author-typed text, rendered as text. `pre-wrap` keeps the
                                  * line breaks they typed; `dangerouslySetInnerHTML` would
                                  * make this field stored XSS against every enrolled learner.
                                  */}
                                {row.body ? (
                                    <Typography sx={{
                                        ...vlearnType.body, mt: 1, whiteSpace: 'pre-wrap',
                                        overflowWrap: 'anywhere'
                                    }}>
                                        {row.body}
                                    </Typography>
                                ) : null}

                                <Box sx={{
                                    display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.25, flexWrap: 'wrap'
                                }}>
                                    <Avatar
                                        src={authorPhoto || undefined}
                                        sx={{
                                            width: 20, height: 20, fontSize: 9,
                                            bgcolor: 'var(--lightGrey, #ededed)', color: '#6f6f6f'
                                        }}>
                                        {[row.author_prenom, row.author_nom].filter(Boolean)
                                            .map((part) => String(part).charAt(0).toUpperCase()).join('')}
                                    </Avatar>
                                    <Typography sx={{ ...vlearnType.meta }}>
                                        {[row.author_prenom, row.author_nom].filter(Boolean).join(' ')}
                                    </Typography>
                                    {published ? (
                                        <Typography sx={{ ...vlearnType.meta, fontVariantNumeric: 'tabular-nums' }}>
                                            {`· ${published}`}
                                        </Typography>
                                    ) : null}
                                    {emailed ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35 }}>
                                            <MarkEmailReadOutlinedIcon
                                                sx={{ fontSize: 14, color: '#9aa0ad' }} aria-hidden="true" />
                                            <Typography sx={{ ...vlearnType.meta, fontVariantNumeric: 'tabular-nums' }}>
                                                <Langs str="VLEARN_ANNOUNCEMENT_EMAILED_ON"
                                                    params={{ date: emailed }} />
                                            </Typography>
                                        </Box>
                                    ) : null}
                                </Box>

                                {/*
                                  * The read count carries its denominator for the same reason
                                  * the dashboard's rates do: "11" is not a finding, "11 of 14"
                                  * is. The bar is the shape of it at a glance; the sentence
                                  * above it is the fact.
                                  */}
                                {hasReceipts ? (
                                    <Box sx={{ mt: 1.25, maxWidth: 320 }}>
                                        <Typography sx={{
                                            ...vlearnType.meta, mb: 0.5, fontVariantNumeric: 'tabular-nums'
                                        }}>
                                            <Langs str="VLEARN_ANNOUNCEMENT_READ_COUNT"
                                                params={{ read: readCount, total: totalEnrolled }} />
                                        </Typography>
                                        <VLearnProgressBar percent={readPercent} height={5} />
                                    </Box>
                                ) : null}
                            </Box>
                        )
                    })}
                </Box>
            )}

            {/* ── Compose / edit dialog ───────────────────────────────────────────── */}
            {/* Full-screen on a phone. A title, a body textarea and a pin switch inside a
                floating `maxWidth="sm"` paper left the textarea about 260px wide with the
                actions pushed below the fold — an announcement is written, not filled in, and
                it needs the whole screen. */}
            <Dialog
                open={composeOpen}
                onClose={closeCompose}
                fullWidth
                maxWidth="sm"
                fullScreen={isSmall}
                PaperProps={isSmall ? {
                    sx: {
                        paddingTop: 'env(safe-area-inset-top, 0px)',
                        paddingBottom: 'env(safe-area-inset-bottom, 0px)'
                    }
                } : undefined}
            >
                <DialogTitle component="h2" sx={{ ...vlearnType.sectionTitle }}>
                    <Langs str={form.id ? "VLEARN_ANNOUNCEMENT_UPDATE" : "VLEARN_ANNOUNCEMENT_NEW"} />
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <TextField
                        label={Localize("VLEARN_ANNOUNCEMENT_TITLE_LABEL", lang)}
                        value={form.title}
                        onChange={(event) => {
                            setForm((current) => ({ ...current, title: event.target.value }))
                            setTitleError(false)
                        }}
                        error={titleError}
                        helperText={titleError
                            ? Localize("VLEARN_ANNOUNCEMENT_TITLE_REQUIRED", lang) : ' '}
                        // 190 is the column width; clipping here rather than letting the
                        // server silently truncate what the author typed.
                        inputProps={{ maxLength: 190 }}
                        size="small"
                        fullWidth
                        autoFocus
                    />

                    <TextField
                        label={Localize("VLEARN_ANNOUNCEMENT_BODY_LABEL", lang)}
                        value={form.body}
                        onChange={(event) =>
                            setForm((current) => ({ ...current, body: event.target.value }))}
                        helperText={Localize("VLEARN_ANNOUNCEMENT_BODY_HELP", lang)}
                        multiline
                        minRows={5}
                        size="small"
                        fullWidth
                    />

                    <Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.is_pinned}
                                    onChange={(event) =>
                                        setForm((current) => ({ ...current, is_pinned: event.target.checked }))}
                                />
                            }
                            label={<Langs str="VLEARN_ANNOUNCEMENT_PINNED" />}
                        />
                        <FormHelperText sx={{ mt: 0 }}>
                            <Langs str="VLEARN_ANNOUNCEMENT_PINNED_HELP" />
                        </FormHelperText>
                    </Box>

                    <Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.notify_email}
                                    onChange={(event) =>
                                        setForm((current) => ({ ...current, notify_email: event.target.checked }))}
                                />
                            }
                            label={<Langs str="VLEARN_ANNOUNCEMENT_NOTIFY_EMAIL" />}
                        />
                        <FormHelperText sx={{ mt: 0 }}>
                            <Langs str="VLEARN_ANNOUNCEMENT_NOTIFY_HELP" />
                        </FormHelperText>
                        {/* The module-level switch is the one that actually gates the send, so
                            an author must be told when it is off rather than watching a mail
                            that never arrives. */}
                        {!emailEnabled ? (
                            <FormHelperText sx={{ mt: 0.5, color: '#B25E00' }}>
                                <Langs str="VLEARN_ANNOUNCEMENT_NOTIFY_DISABLED_HELP" />
                            </FormHelperText>
                        ) : null}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ gap: 1, px: { xs: 2, sm: 3 }, py: 1.5 }}>
                    <Button onClick={closeCompose} sx={{ textTransform: 'none', minHeight: 44 }}>
                        <Langs str="VLEARN_CANCEL" />
                    </Button>
                    <Button variant="contained" onClick={onSave}
                        sx={{ textTransform: 'none', minHeight: 44 }}>
                        <Langs str={form.id
                            ? "VLEARN_ANNOUNCEMENT_UPDATE" : "VLEARN_ANNOUNCEMENT_PUBLISH"} />
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Delete confirmation ─────────────────────────────────────────────── */}
            <Dialog open={Boolean(pendingDelete)} onClose={() => setPendingDelete(null)}>
                <DialogTitle component="h2" sx={{ ...vlearnType.sectionTitle }}>
                    <Langs str="VLEARN_ANNOUNCEMENT_DELETE_TITLE" />
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ ...vlearnType.body }}>
                        <Langs str="VLEARN_ANNOUNCEMENT_DELETE_BODY" />
                    </DialogContentText>
                </DialogContent>
                {/* `gap: 2` rather than MUI's 8px: a destructive confirm sitting a
                    thumb's-width from Cancel is a mis-tap that deletes something. */}
                <DialogActions sx={{ gap: 2, px: { xs: 2, sm: 3 }, py: 1.5 }}>
                    <Button onClick={() => setPendingDelete(null)}
                        sx={{ textTransform: 'none', minHeight: 44 }}>
                        <Langs str="VLEARN_CANCEL" />
                    </Button>
                    <Button color="error" variant="contained" onClick={onConfirmDelete}
                        sx={{ textTransform: 'none', minHeight: 44 }}>
                        <Langs str="VLEARN_DELETE" />
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
